import { useEffect, useMemo, useRef, useState } from 'react';
import type { PublicSnapshot } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { copyToClipboard, extractErrorCode } from '../lib/dom.js';
import { buildShareUrl } from '../lib/share.js';
import { useSession } from '../state/session.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Modal } from './Modal.js';

export interface ShareDestination {
  kind: 'friend' | 'group';
  id: string;
  name: string;
  photoUrl: string | null;
  initials: string;
}

export function friendToDestination(friend: {
  id: string;
  publicId: number;
  firstName: string | null;
  username: string | null;
  photoUrl: string | null;
}): ShareDestination {
  const name = friend.firstName || (friend.username ? `@${friend.username}` : `ID ${friend.publicId}`);
  const initials =
    (friend.firstName || friend.username || String(friend.publicId))
      .split(/[\s_]+/)
      .slice(0, 2)
      .map((p) => p.slice(0, 1))
      .join('')
      .toUpperCase() || '?';
  return {
    kind: 'friend',
    id: friend.id,
    name,
    photoUrl: friend.photoUrl,
    initials,
  };
}

export function groupToDestination(group: { id: string; title: string }): ShareDestination {
  const initials =
    group.title
      .split(/[\s_]+/)
      .slice(0, 2)
      .map((p) => p.slice(0, 1))
      .join('')
      .toUpperCase() || 'G';
  return {
    kind: 'group',
    id: group.id,
    name: group.title,
    photoUrl: null,
    initials,
  };
}

export interface ShareModalProps {
  conversationId: string | null;
  messageId?: string | null;
  messageIds?: string[];
  onSelectMessages?: () => void;
  onForwarded?: () => void;
  onClose: () => void;
}

export type ShareModalViewState = 'loading' | 'ready' | 'create';

export function getShareModalViewState(
  loading: boolean,
  snapshot: PublicSnapshot | null,
): ShareModalViewState {
  if (loading) return 'loading';
  return snapshot ? 'ready' : 'create';
}

export function getShareErrorKey(error: unknown): Parameters<ReturnType<typeof useI18n>['t']>[0] {
  return ('error.' + extractErrorCode(error)) as Parameters<ReturnType<typeof useI18n>['t']>[0];
}

export function getShareRequestKey(
  conversationId: string,
  messageIds: string[],
  destinationKey: string,
): string {
  return JSON.stringify([conversationId, [...messageIds].sort(), destinationKey]);
}

export function finishShareRequest(
  requestIds: Map<string, string>,
  requestKey: string,
  requestId: string,
  isCurrent: boolean,
): void {
  // A dismissed request may already have committed; reopening must retry its ID.
  if (isCurrent && requestIds.get(requestKey) === requestId) requestIds.delete(requestKey);
}

export function ShareModal({ conversationId, messageId, messageIds, onSelectMessages, onForwarded, onClose }: ShareModalProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const [snapshot, setSnapshot] = useState<PublicSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const operationVersionRef = useRef(0);
  const sendingRef = useRef(false);
  const forwardRequestIdsRef = useRef(new Map<string, string>());
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchMode = messageIds !== undefined;
  const messageIdsKey = messageIds?.join(',') ?? '';

  // Chat/group destinations state
  const [destinations, setDestinations] = useState<ShareDestination[]>([]);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [destinationError, setDestinationError] = useState<string | null>(null);
  const [sendingDestinationId, setSendingDestinationId] = useState<string | null>(null);
  const [sentDestinations, setSentDestinations] = useState<Set<string>>(new Set());
  const [sendSuccessMessage, setSendSuccessMessage] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const shareUrl = useMemo(
    () => (snapshot ? buildShareUrl(window.location.origin, snapshot.token) : ''),
    [snapshot],
  );

  useEffect(() => {
    operationVersionRef.current += 1;
    sendingRef.current = false;
    setShowConversation(false);
    setConfirmRevoke(false);
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    setSubmitting(false);
    setLoading(false);
    setLoadingDestinations(false);
    if (!conversationId) {
      setSnapshot(null);
      setCopied(false);
      setError(null);
      setConfirmRevoke(false);
      setDestinations([]);
      setDestinationError(null);
      setSendSuccessMessage(null);
      setSendError(null);
      setSentDestinations(new Set());
      setSendingDestinationId(null);
      return;
    }
    let cancelled = false;
    setLoading(!batchMode);
    setError(null);
    setCopied(false);
    setSnapshot(null);
    if (!batchMode) void api
      .getShare(conversationId)
      .then(({ snapshot: current }) => {
        if (!cancelled) setSnapshot(current);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(t(getShareErrorKey(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Load personal chat and group destinations
    setLoadingDestinations(true);
    setDestinationError(null);
    setSendSuccessMessage(null);
    setSendError(null);
    setSentDestinations(new Set());
    setSendingDestinationId(null);

    Promise.all([
      api.listDirectChatFriends().catch(() => null),
      api.listDirectChatGroups().catch(() => null),
    ])
      .then(([friendsRes, groupsRes]) => {
        if (cancelled) return;
        if (!friendsRes && !groupsRes) {
          setDestinationError(t('common.error'));
          return;
        }
        const items: ShareDestination[] = [];
        if (friendsRes?.friends) {
          for (const friend of friendsRes.friends) {
            items.push(friendToDestination(friend));
          }
        }
        if (groupsRes?.groups) {
          for (const group of groupsRes.groups) {
            items.push(groupToDestination(group));
          }
        }
        setDestinations(items);
      })
      .finally(() => {
        if (!cancelled) setLoadingDestinations(false);
      });

    return () => {
      cancelled = true;
      operationVersionRef.current += 1;
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    };
  }, [api, conversationId, messageId, messageIdsKey, batchMode, t]);

  async function handleSendToDestination(dest: ShareDestination) {
    if (!conversationId || (batchMode ? !messageIds?.length : !messageId)) return;
    const destKey = `${dest.kind}:${dest.id}`;
    if (sendingRef.current || sentDestinations.has(destKey)) return;
    sendingRef.current = true;
    const version = operationVersionRef.current;

    setSendingDestinationId(destKey);
    setSendError(null);
    setSendSuccessMessage(null);

    try {
      const target = dest.kind === 'friend' ? { friendId: dest.id } : { groupId: dest.id };
      const requestKey = getShareRequestKey(
        conversationId, batchMode ? messageIds! : [messageId!], destKey,
      );
      let requestId = forwardRequestIdsRef.current.get(requestKey);
      if (!requestId) {
        requestId = crypto.randomUUID();
        forwardRequestIdsRef.current.set(requestKey, requestId);
      }
      if (batchMode) {
        await api.forwardChatMessages({
          sourceKind: 'conversation',
          sourceConversationId: conversationId,
          messageIds: messageIds!,
          targetKind: dest.kind === 'friend' ? 'direct' : 'group',
          targetConversationId: dest.id,
          clientRequestId: requestId,
        });
      } else {
        await api.shareAiResponse(target, conversationId, messageId!, requestId);
      }
      finishShareRequest(
        forwardRequestIdsRef.current, requestKey, requestId,
        version === operationVersionRef.current,
      );
      if (version !== operationVersionRef.current) return;
      setSentDestinations((prev) => new Set(prev).add(destKey));
      setSendSuccessMessage(t('share.sentSuccess'));
      if (batchMode) onForwarded?.();
    } catch (err: unknown) {
      if (version === operationVersionRef.current) setSendError(t(getShareErrorKey(err)));
    } finally {
      if (version === operationVersionRef.current) {
        sendingRef.current = false;
        setSendingDestinationId(null);
      }
    }
  }

  async function handleCreate() {
    if (!conversationId || submitting) return;
    setSubmitting(true);
    const version = operationVersionRef.current;
    setError(null);
    try {
      const result = await api.createShare(conversationId);
      if (version === operationVersionRef.current) setSnapshot(result.snapshot);
    } catch (err: unknown) {
      if (version === operationVersionRef.current) setError(t(getShareErrorKey(err)));
    } finally {
      if (version === operationVersionRef.current) setSubmitting(false);
    }
  }

  async function handleCopy() {
    if (!shareUrl) return;
    const version = operationVersionRef.current;
    const ok = await copyToClipboard(shareUrl);
    if (!ok || version !== operationVersionRef.current) return;
    setCopied(true);
    if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
  }

  async function handleRevoke() {
    if (!conversationId || submitting) return;
    setSubmitting(true);
    const version = operationVersionRef.current;
    setError(null);
    try {
      await api.revokeShare(conversationId);
      if (version !== operationVersionRef.current) return;
      setSnapshot(null);
      setCopied(false);
      setConfirmRevoke(false);
    } catch (err: unknown) {
      if (version === operationVersionRef.current) setError(t(getShareErrorKey(err)));
    } finally {
      if (version === operationVersionRef.current) setSubmitting(false);
    }
  }

  const viewState = getShareModalViewState(loading, snapshot);

  return (
    <>
      <Modal
        open={conversationId !== null}
        onClose={() => {
          operationVersionRef.current += 1;
          onClose();
        }}
        title={t('share.title')}
        closeLabel={t('common.close')}
        className="share-modal"
      >
        <div className="share-modal-body">
          {!batchMode && onSelectMessages ? (
            <div className="share-modal-actions">
              <button type="button" className="btn btn-primary" disabled={sendingDestinationId !== null} onClick={onSelectMessages}>
                {t('share.selectMessages')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowConversation(true)}>
                {t('share.conversation')}
              </button>
            </div>
          ) : null}
          {batchMode ? <p role="status">{t('share.selectedCount', { count: String(messageIds?.length ?? 0) })}</p> : null}
          {/* Top row: Chat & group destinations */}
          <div className="share-destinations-section">
            <h3 className="share-destinations-title">{t('share.sendToChat')}</h3>
            {loadingDestinations ? (
              <div className="share-destinations-loading">
                <span className="spinner" />
                <span>{t('share.loadingDestinations')}</span>
              </div>
            ) : destinationError ? (
              <p className="bubble-error">{destinationError}</p>
            ) : destinations.length === 0 ? (
              <p className="share-destinations-empty">{t('share.noDestinations')}</p>
            ) : (
              <div
                className="share-destinations-row"
                role="region"
                aria-label={t('share.sendToChat')}
                tabIndex={0}
              >
                {destinations.map((dest) => {
                  const destKey = `${dest.kind}:${dest.id}`;
                  const isSending = sendingDestinationId === destKey;
                  const isSent = sentDestinations.has(destKey);
                  return (
                    <button
                      key={destKey}
                      type="button"
                      className="share-destination-item"
                      disabled={(batchMode ? !messageIds?.length : !messageId) || sendingDestinationId !== null || isSent}
                      onClick={() => void handleSendToDestination(dest)}
                      aria-label={`${dest.name} - ${t('share.sendToChat')}`}
                    >
                      <div className="share-destination-avatar-wrap">
                        {dest.photoUrl ? (
                          <img
                            className="direct-chat-avatar share-destination-avatar"
                            src={dest.photoUrl}
                            alt=""
                          />
                        ) : (
                          <span className="direct-chat-avatar direct-chat-avatar-fallback share-destination-avatar">
                            {dest.initials}
                          </span>
                        )}
                        {isSending && (
                          <span className="share-destination-badge share-destination-badge--sending">
                            <span className="spinner" />
                          </span>
                        )}
                        {isSent && (
                          <span
                            className="share-destination-badge share-destination-badge--sent"
                            aria-label={t('share.sentSuccess')}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                      <span className="share-destination-name" title={dest.name}>
                        {dest.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {sendingDestinationId ? (
              <p className="share-destinations-status share-destinations-status--sending" role="status">
                {t('share.sending')}
              </p>
            ) : null}
            {sendSuccessMessage ? (
              <p className="share-destinations-status share-destinations-status--success" role="status">
                {sendSuccessMessage}
              </p>
            ) : null}
            {sendError ? (
              <p className="bubble-error" role="alert">
                {sendError}
              </p>
            ) : null}
          </div>

          {/* Link sharing */}
          {batchMode || (onSelectMessages && !showConversation) ? null : viewState === 'loading' ? (
            <p>{t('share.loading')}</p>
          ) : viewState === 'ready' ? (
            <>
              <label className="share-modal-link-group">
                <span>{t('share.sharedBy')}</span>
                <input
                  className="field"
                  value={shareUrl}
                  readOnly
                  onFocus={(event) => event.target.select()}
                />
              </label>
              <div className="share-modal-actions">
                <button type="button" className="btn btn-primary" onClick={() => void handleCopy()}>
                  {copied ? t('share.copied') : t('share.copyLink')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={submitting}
                  onClick={() => setConfirmRevoke(true)}
                >
                  {t('share.revoke')}
                </button>
              </div>
            </>
          ) : (
            <div className="share-modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={submitting}
                onClick={() => void handleCreate()}
              >
                {t('share.createLink')}
              </button>
            </div>
          )}
          {error ? <p className="bubble-error">{error}</p> : null}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmRevoke}
        title={t('share.title')}
        message={t('share.revokeConfirm')}
        confirmLabel={t('share.revoke')}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setConfirmRevoke(false)}
      />
    </>
  );
}
